import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const BalanceTransferCalculator = () => {
  const content = article.find((item) => item.id === 2);
  const [cards, setCards] = useState([
    {
      id: Date.now(),
      name: "Credit Card 1",
      balance: "5000",
      currentApr: "18.99",
      newApr: "0",
      transferFeePercent: "3",
      monthlyPayment: "200",
      paymentFrequency: "Monthly",
      promoDuration: "18",
      fees: "0",
    },
  ]);

  const [results, setResults] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [startDate, setStartDate] = useState("");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const resultsRef = useRef();

  // Set default start date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = cards.some(card => 
      card.balance && card.currentApr && card.monthlyPayment
    );
    if (hasValidData) {
      setTimeout(() => {
        calculateSavings();
      }, 100);
    }
  }, [cards, startDate]);

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: `Credit Card ${cards.length + 1}`,
      balance: "3000",
      currentApr: "22.99",
      newApr: "0",
      transferFeePercent: "3",
      monthlyPayment: "150",
      paymentFrequency: "Monthly",
      promoDuration: "12",
      fees: "0",
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const handleCardChange = (id, field, value) => {
    const newCards = cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    );
    setCards(newCards);
  };

  const resetCalculator = () => {
    setCards([{
      id: Date.now(),
      name: "Credit Card 1",
      balance: "5000",
      currentApr: "18.99",
      newApr: "0",
      transferFeePercent: "3",
      monthlyPayment: "200",
      paymentFrequency: "Monthly",
      promoDuration: "18",
      fees: "0",
    }]);
    setResults(null);
    setScenarios(null);
    setActiveCardIndex(0);
    setStartDate(new Date().toISOString().split('T')[0]);
  };

  const calculateSavings = () => {
    const validCards = cards.filter(card => 
      card.balance && card.currentApr && card.monthlyPayment
    );

    if (validCards.length === 0) {
      alert("Please fill in required fields for at least one card (Balance, Current APR, and Monthly Payment)");
      return;
    }

    const cardResults = validCards.map((card) => {
      const balance = parseFloat(card.balance) || 0;
      const currentApr = parseFloat(card.currentApr) / 100 || 0;
      const newApr = parseFloat(card.newApr) / 100 || 0;
      const transferFee = parseFloat(card.transferFeePercent) / 100 || 0;
      const monthlyPayment = parseFloat(card.monthlyPayment) || 0;
      const promoMonths = parseInt(card.promoDuration) || 0;
      const fees = parseFloat(card.fees) || 0;

      if (!balance || !monthlyPayment) return null;

      // Calculate payoff without transfer (current card)
      let remainingCurrent = balance;
      let totalInterestCurrent = 0;
      let monthsCurrent = 0;
      const breakdownCurrent = [];

      while (remainingCurrent > 0 && monthsCurrent < 600) {
        const interest = remainingCurrent * (currentApr / 12);
        totalInterestCurrent += interest;
        const principalPaid = Math.min(monthlyPayment - interest, remainingCurrent);
        remainingCurrent -= principalPaid;
        monthsCurrent++;
        
        breakdownCurrent.push({
          month: monthsCurrent,
          interest: interest.toFixed(2),
          principal: principalPaid.toFixed(2),
          remaining: Math.max(remainingCurrent, 0).toFixed(2),
          apr: (currentApr * 100).toFixed(2)
        });

        if (remainingCurrent <= 0) break;
      }

      // Calculate payoff with transfer (new card)
      const transferFeeAmount = balance * transferFee;
      let remainingNew = balance + transferFeeAmount;
      let totalInterestNew = 0;
      let monthsNew = 0;
      const breakdownNew = [];

      while (remainingNew > 0 && monthsNew < 600) {
        const currentRate = monthsNew < promoMonths ? newApr : currentApr;
        const interest = remainingNew * (currentRate / 12);
        totalInterestNew += interest;
        const principalPaid = Math.min(monthlyPayment - interest, remainingNew);
        remainingNew -= principalPaid;
        monthsNew++;
        
        breakdownNew.push({
          month: monthsNew,
          interest: interest.toFixed(2),
          principal: principalPaid.toFixed(2),
          remaining: Math.max(remainingNew, 0).toFixed(2),
          apr: (currentRate * 100).toFixed(2),
          isPromo: monthsNew <= promoMonths
        });

        if (remainingNew <= 0) break;
      }

      const netSavings = totalInterestCurrent - totalInterestNew - transferFeeAmount - fees;
      const totalFees = transferFeeAmount + fees;

      // Calculate debt-free dates
      const start = startDate ? new Date(startDate) : new Date();
      const debtFreeCurrent = new Date(start);
      debtFreeCurrent.setMonth(debtFreeCurrent.getMonth() + monthsCurrent);
      
      const debtFreeNew = new Date(start);
      debtFreeNew.setMonth(debtFreeNew.getMonth() + monthsNew);

      return {
        name: card.name,
        interestSaved: netSavings.toFixed(2),
        totalFees: totalFees.toFixed(2),
        payoffMonthsCurrent: monthsCurrent,
        payoffMonthsNew: monthsNew,
        totalInterestCurrent: totalInterestCurrent.toFixed(2),
        totalInterestNew: totalInterestNew.toFixed(2),
        transferFeeAmount: transferFeeAmount.toFixed(2),
        debtFreeCurrent: debtFreeCurrent.toLocaleDateString(),
        debtFreeNew: debtFreeNew.toLocaleDateString(),
        monthlyBreakdownCurrent: breakdownCurrent,
        monthlyBreakdownNew: breakdownNew,
        netSavings: netSavings.toFixed(2),
        isWorthwhile: netSavings > 0
      };
    }).filter(result => result !== null);

    // Calculate scenarios
    calculateScenarios(validCards[0], cardResults[0]);

    setResults(cardResults);
  };

  const calculateScenarios = (primaryCard, currentResult) => {
    if (!primaryCard?.balance || !primaryCard?.currentApr || !currentResult) return;

    const balance = parseFloat(primaryCard.balance);
    const currentApr = parseFloat(primaryCard.currentApr) / 100;
    const basePayment = parseFloat(primaryCard.monthlyPayment) || 0;
    const transferFeePercent = parseFloat(primaryCard.transferFeePercent) / 100 || 0;
    const newApr = parseFloat(primaryCard.newApr) / 100 || 0;
    const promoMonths = parseInt(primaryCard.promoDuration) || 0;

    const scenarioTypes = [
      { name: "Current Card", type: "current", multiplier: 1 },
      { name: "Balance Transfer", type: "transfer", multiplier: 1 },
      { name: "Pay 25% More", type: "current", multiplier: 1.25 },
      { name: "Double Payments", type: "current", multiplier: 2 },
    ];

    const scenarioResults = scenarioTypes.map(scenario => {
      const payment = basePayment * scenario.multiplier;
      let remaining = balance;
      let totalInterest = 0;
      let payoffMonths = 0;
      let transferFee = 0;

      if (scenario.type === "transfer") {
        transferFee = balance * transferFeePercent;
        remaining = balance + transferFee;
      }

      while (remaining > 0 && payoffMonths < 600) {
        let currentRate = currentApr;
        if (scenario.type === "transfer") {
          currentRate = payoffMonths < promoMonths ? newApr : currentApr;
        }
        
        const interest = remaining * (currentRate / 12);
        totalInterest += interest;
        const principalPaid = Math.min(payment - interest, remaining);
        remaining -= principalPaid;
        payoffMonths++;
        
        if (remaining <= 0) break;
      }

      const totalCost = balance + totalInterest + transferFee;

      return {
        name: scenario.name,
        type: scenario.type,
        monthlyPayment: payment.toFixed(2),
        totalInterest: totalInterest.toFixed(2),
        transferFee: transferFee.toFixed(2),
        payoffMonths,
        totalCost: totalCost.toFixed(2),
      };
    });

    // Calculate savings compared to current plan
    const currentPlanCost = parseFloat(scenarioResults[0].totalCost);
    const updatedScenarioResults = scenarioResults.map(scenario => ({
      ...scenario,
      savings: (currentPlanCost - parseFloat(scenario.totalCost)).toFixed(2)
    }));

    setScenarios(updatedScenarioResults);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const savingsComparisonData = results && results[0] ? {
    labels: ['Current Card', 'Balance Transfer'],
    datasets: [
      {
        label: 'Total Interest',
        data: [
          parseFloat(results[0].totalInterestCurrent),
          parseFloat(results[0].totalInterestNew)
        ],
        backgroundColor: ['#A03B00', '#F06201'],
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Fees',
        data: [
          0,
          parseFloat(results[0].transferFeeAmount)
        ],
        backgroundColor: ['#FF8C42', '#FFB088'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const payoffComparisonData = results && results[0] ? {
    labels: ['Current Card', 'Balance Transfer'],
    datasets: [
      {
        label: 'Payoff Time (Months)',
        data: [results[0].payoffMonthsCurrent, results[0].payoffMonthsNew],
        backgroundColor: '#F06201',
        borderColor: '#F06201',
        borderWidth: 2,
      }
    ],
  } : null;

  const monthlyComparisonData = results && results[0] ? {
    labels: results[0].monthlyBreakdownCurrent.slice(0, 12).map(month => `Month ${month.month}`),
    datasets: [
      {
        label: 'Current Card Interest',
        data: results[0].monthlyBreakdownCurrent.slice(0, 12).map(month => parseFloat(month.interest)),
        borderColor: '#A03B00',
        backgroundColor: 'rgba(160, 59, 0, 0.1)',
        borderWidth: 2,
        fill: true,
      },
      {
        label: 'Transfer Card Interest',
        data: results[0].monthlyBreakdownNew.slice(0, 12).map(month => parseFloat(month.interest)),
        borderColor: '#F06201',
        backgroundColor: 'rgba(240, 98, 1, 0.1)',
        borderWidth: 2,
        fill: true,
      }
    ],
  } : null;

  const scenarioComparisonData = scenarios ? {
    labels: scenarios.map(scenario => scenario.name),
    datasets: [
      {
        label: 'Payoff Time (Months)',
        data: scenarios.map(scenario => scenario.payoffMonths),
        backgroundColor: '#FF8C42',
        borderColor: '#FF8C42',
        borderWidth: 1,
      },
      {
        label: 'Total Cost ($)',
        data: scenarios.map(scenario => parseFloat(scenario.totalCost)),
        backgroundColor: '#A03B00',
        borderColor: '#A03B00',
        borderWidth: 1,
      }
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const totalBalance = cards.reduce((sum, card) => sum + (parseFloat(card.balance) || 0), 0);
  const totalMonthlyPayment = cards.reduce((sum, card) => sum + (parseFloat(card.monthlyPayment) || 0), 0);

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Balance Transfer Savings Calculator</h1>
          <p>Calculate potential savings from transferring credit card balances to lower APR cards</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Balance:</span>
              <strong>${totalBalance.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total Monthly Payment:</span>
              <strong>${totalMonthlyPayment.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Cards:</span>
              <strong>{cards.length}</strong>
            </div>
            {results && results[0] && (
              <div className="summary-item">
                <span>Potential Savings:</span>
                <strong className={results[0].isWorthwhile ? "savings" : ""}>
                  ${Math.abs(results[0].netSavings).toFixed(2)}
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Credit Card Details</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  <i className="fa-solid fa-plus"></i> Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Compare balance transfer options to maximize interest savings and reduce payoff time
              </div>
            </div>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Current Balance ($)*</label>
                      <input
                        type="number"
                        value={card.balance}
                        onChange={(e) => handleCardChange(card.id, "balance", e.target.value)}
                        placeholder="5000"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Current APR (%)*</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.currentApr}
                        onChange={(e) => handleCardChange(card.id, "currentApr", e.target.value)}
                        placeholder="18.99"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Transfer APR (%)*</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.newApr}
                        onChange={(e) => handleCardChange(card.id, "newApr", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Transfer Fee (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.transferFeePercent}
                        onChange={(e) => handleCardChange(card.id, "transferFeePercent", e.target.value)}
                        placeholder="3.00"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Monthly Payment ($)*</label>
                      <input
                        type="number"
                        value={card.monthlyPayment}
                        onChange={(e) => handleCardChange(card.id, "monthlyPayment", e.target.value)}
                        placeholder="200"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Promo Period (Months)</label>
                      <input
                        type="number"
                        value={card.promoDuration}
                        onChange={(e) => handleCardChange(card.id, "promoDuration", e.target.value)}
                        placeholder="18"
                        min="0"
                        max="600"
                      />
                    </div>

                    <div className="input-group">
                      <label>Annual Fees ($)</label>
                      <input
                        type="number"
                        value={card.fees}
                        onChange={(e) => handleCardChange(card.id, "fees", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Payment Frequency</label>
                      <select 
                        value={card.paymentFrequency} 
                        onChange={(e) => handleCardChange(card.id, "paymentFrequency", e.target.value)}
                      >
                        <option value="Monthly">Monthly</option>
                        <option value="Bi-weekly">Bi-weekly</option>
                        <option value="Weekly">Weekly</option>
                      </select>
                    </div>
                  </div>
                  <div className="required-note">* Required fields</div>
                </div>
              ))}
            </div>
          </div>

          <div className="payment-settings">
            <div className="section-header">
              <h2>Calculation Settings</h2>
            </div>
            <div className="settings-grid">
              <div className="input-group">
                <label>Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
              </div>
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateSavings}>
              Calculate Savings
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Balance Transfer Analysis</h2>
            <div className="default-message">
              <p>Enter your credit card details to compare balance transfer options and calculate potential savings.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Balance Transfer Tips:</h3>
                <ul>
                  <li>Look for cards with 0% introductory APR offers for maximum savings</li>
                  <li>Calculate if transfer fees outweigh interest savings</li>
                  <li>Pay off the balance before the promotional period ends</li>
                  <li>Consider your credit score impact from opening new accounts</li>
                  <li>Continue making the same monthly payments to pay down debt faster</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results && results.length > 0 ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Balance Transfer Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Net Savings</h3>
                  <div className={`summary-value ${results[0].isWorthwhile ? "savings" : ""}`}>
                    ${Math.abs(results[0].netSavings).toFixed(2)}
                  </div>
                  <div className="summary-note">
                    {results[0].isWorthwhile ? "Transfer recommended" : "Not recommended"}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-calendar-days"></i></div>
                  <h3>Months Saved</h3>
                  <div className="summary-value">
                    {Math.max(0, results[0].payoffMonthsCurrent - results[0].payoffMonthsNew)}
                  </div>
                  <div className="summary-note">Faster payoff time</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
                  <h3>Interest Savings</h3>
                  <div className="summary-value">${Math.abs(results[0].interestSaved).toFixed(2)}</div>
                  <div className="summary-note">Total interest saved</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-scale-balanced"></i></div>
                  <h3>Transfer Fees</h3>
                  <div className="summary-value">${results[0].transferFeeAmount}</div>
                  <div className="summary-note">One-time fee</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Cost Comparison</h3>
                    {savingsComparisonData && <Bar data={savingsComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Cost Breakdown:</strong> Compare total interest and fees between keeping your current card vs. transferring.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Payoff Time Comparison</h3>
                    {payoffComparisonData && <Bar data={payoffComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Time Savings:</strong> See how much faster you can pay off your debt with a balance transfer.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Monthly Interest Comparison</h3>
                    {monthlyComparisonData && <Line data={monthlyComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Interest Over Time:</strong> Track how monthly interest payments differ between the two options.</p>
                    </div>
                  </div>

                  {scenarios && (
                    <div className="chart-card">
                      <h3>Payment Strategy Comparison</h3>
                      <Bar data={scenarioComparisonData} options={chartOptions} />
                      <div className="chart-description">
                        <p><strong>Strategy Analysis:</strong> Compare different payment approaches to find your optimal path.</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.map((result, index) => (
                      <div key={index} className="result-card">
                        <h3>{result.name} - Current vs Transfer</h3>
                        <div className="result-stats">
                          <div className="stat">
                            <label>Original Balance</label>
                            <span className="stat-value">${parseFloat(cards[index].balance).toFixed(2)}</span>
                          </div>
                          <div className="stat">
                            <label>Current Card Total Interest</label>
                            <span className="stat-value">${result.totalInterestCurrent}</span>
                          </div>
                          <div className="stat">
                            <label>Transfer Card Total Interest</label>
                            <span className="stat-value">${result.totalInterestNew}</span>
                          </div>
                          <div className="stat">
                            <label>Transfer Fee</label>
                            <span className="stat-value">${result.transferFeeAmount}</span>
                          </div>
                          <div className="stat">
                            <label>Net Savings</label>
                            <span className={`stat-value ${result.isWorthwhile ? "savings" : ""}`}>
                              ${result.netSavings}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Current Payoff Time</label>
                            <span className="stat-value">{result.payoffMonthsCurrent} months</span>
                          </div>
                          <div className="stat">
                            <label>Transfer Payoff Time</label>
                            <span className="stat-value">{result.payoffMonthsNew} months</span>
                          </div>
                          <div className="stat">
                            <label>Current Debt-Free Date</label>
                            <span className="stat-value">{result.debtFreeCurrent}</span>
                          </div>
                          <div className="stat">
                            <label>Transfer Debt-Free Date</label>
                            <span className="stat-value">{result.debtFreeNew}</span>
                          </div>
                        </div>
                        
                        <div className="monthly-breakdown">
                          <h4>Monthly Breakdown (First 12 Months)</h4>
                          <div className="breakdown-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Month</th>
                                  <th>Current Interest</th>
                                  <th>Transfer Interest</th>
                                  <th>Transfer APR</th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.monthlyBreakdownCurrent.slice(0, 12).map((month, i) => (
                                  <tr key={i}>
                                    <td>{month.month}</td>
                                    <td>${month.interest}</td>
                                    <td>${result.monthlyBreakdownNew[i]?.interest || "0.00"}</td>
                                    <td>{result.monthlyBreakdownNew[i]?.apr || "0.00"}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Scenario Comparison */}
                  {scenarios && (
                    <div className="scenario-comparison">
                      <h3>Payment Strategy Analysis</h3>
                      <div className="scenario-grid">
                        {scenarios.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name}</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>Monthly Payment</label>
                                <span>${scenario.monthlyPayment}</span>
                              </div>
                              <div className="stat">
                                <label>Payoff Time</label>
                                <span>{scenario.payoffMonths} months</span>
                              </div>
                              <div className="stat">
                                <label>Total Interest</label>
                                <span>${scenario.totalInterest}</span>
                              </div>
                              {scenario.transferFee > 0 && (
                                <div className="stat">
                                  <label>Transfer Fee</label>
                                  <span>${scenario.transferFee}</span>
                                </div>
                              )}
                              <div className="stat">
                                <label>Total Cost</label>
                                <span>${scenario.totalCost}</span>
                              </div>
                              {scenario.savings !== "0.00" && (
                                <div className="stat">
                                  <label>Savings vs Current</label>
                                  <span className={parseFloat(scenario.savings) > 0 ? "savings" : ""}>
                                    ${scenario.savings}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your credit card details to compare balance transfer options and calculate savings.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default BalanceTransferCalculator;